// The first reply owns the ThreadInfo ↔ reply separator as its top border, including
// while a just-posted reply is still optimistic (isLocal). Ownership must not depend
// on transient answer state: the suppression added in #2329 assumed a ThreadInfo
// bottom border that #2348 later deleted, leaving optimistic replies with no divider.
// tests/unit/answerBorderPairInvariant.test.ts enumerates the owners at other boundaries.
export const shouldShowFirstAnswerTopBorder = ({ isFirst = false } = {}) => Boolean(isFirst)

// The title draws its bottom divider only when reply rows follow it (the ThreadInfo
// strip renders only then). On an empty thread the row below the title is the reply
// composer or the locked-replies notice, and that row owns the separator as its top
// border — same downward ownership as every other boundary. Under the bottom editor
// neither is there: the composer floats at the panel's foot, the notice inside it,
// and the card ends in blank reserve. The empty thread's title is then its last row
// and draws the line under it, as the last reply does (shouldShowLastAnswerBottomBorder).
export const shouldShowTitleAnswerBottomBorder = ({
    isCardBottom = false,
    lightweightStreamCard = false,
    composerFloats = false,
} = {}) => Boolean((!isCardBottom || composerFloats) && !lightweightStreamCard)

// The last reply owns the reply-list ↔ composer separator as its bottom border —
// except when the composer is answering that very reply, where the avatar-gutter
// connector runs down through the boundary and owns it instead. Exactly one of the
// two, never both and never neither: a rule drawn across the line would cut the
// pair in two, and dropping the border without the line would leave the seam
// unowned. lib/quoteMain.ts decides whether the connector is there.
export const shouldShowLastAnswerBottomBorder = ({
    isTitle = false,
    isLast = false,
    composerConnectsBelow = false,
} = {}) => Boolean(!isTitle && isLast && !composerConnectsBelow)

// Every boundary between two reply rows is owned exactly once, the same way the
// reply-list ↔ composer seam is: by the avatar-gutter connector when the lower
// row answers the upper one, and by a hairline when it does not. Before
// quote_main there was no connector and so no ownership question — rows simply
// ran together — which is why this is gated on the flag by its caller.
//
// The first reply is excluded: its top border already owns the ThreadInfo ↔
// reply boundary, and the connector never crosses the head. A row with nothing
// above it in the list (an embed, a lone preview) has no boundary to own.
export const shouldShowReplySeparatorTopBorder = ({
    isTitle = false,
    isFirst = false,
    hasPreviousRow = false,
    connectsUp = false,
} = {}) => Boolean(!isTitle && !isFirst && hasPreviousRow && !connectsUp)
